import { useCallback } from 'react';
import * as React from 'react';
import tw from 'twin.macro';
import 'styled-components/macro';

import { TailwindTransition } from 'component-lib/atoms/tailwind-transition/TailwindTransition';

import { useAccordionItemContext } from './AccordionItemContext';

export interface AccordionPanelProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Section of content associated with an accordion header.
 */
export const AccordionPanel = React.forwardRef<HTMLDivElement, AccordionPanelProps>(({ children, className }, ref) => {
  const { buttonID, isExpanded, panelID } = useAccordionItemContext('AccordionPanel');

  const collapse = useCallback((node: HTMLElement) => {
    node.style.height = '0';
  }, []);

  const expand = useCallback((node: HTMLElement) => {
    node.style.height = `${node.scrollHeight}px`;
  }, []);

  const reset = useCallback((node: HTMLElement) => {
    node.style.height = '';
  }, []);

  return (
    <TailwindTransition
      in={isExpanded}
      onEnter={collapse}
      onEntered={reset}
      onEntering={expand}
      onExit={expand}
      onExited={reset}
      onExiting={collapse}
      styles={{
        enterActive: tw`duration-fast overflow-hidden transition-all`,
        exitActive: tw`duration-fast overflow-hidden transition-all`,
      }}
    >
      {(status, style) => (
        <div
          aria-labelledby={buttonID}
          className={className}
          css={[tw`text-base text-white`, style]}
          hidden={status === 'exited'}
          id={panelID}
          role="region"
          ref={ref}
        >
          <div tw="p-3">{children}</div>
        </div>
      )}
    </TailwindTransition>
  );
});
